<script setup lang="ts">
import { useAuthStore } from '@/stores/authStore';
import { useSettingsStore } from '@/stores/settings-store';
import { useUserActivityStore } from '@/stores/user-activity-store';
import { TechnicalSettingKeyToId } from '@/utils/constants/system-constants';
import { ScuBottomBar, ScuBottomBarAction, ScuHeadline, ScuModalDialogue } from '@scu/vue';
import { storeToRefs } from 'pinia';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const router = useRouter();
const settingsStore = useSettingsStore();
const maxInactivityInSecs = ref(1000);
const countdownInitial = 30;
let countdownIntervalId: number | undefined;
let inactivityIntervalId: number | undefined;
const authStore = useAuthStore();
const userActivityStore = useUserActivityStore();
const countdown = ref(0);
const isWarningVisible = ref(false);
/*
  "isInFocus" is an indicator of whether the application is running in the background,
  in a minimized window, or otherwise not visible to the user
*/
const isInFocus = ref(true);

watch(isWarningVisible, (newValue) => {
  if (newValue) {
    stopInactivityDetector();
    startCountdown();
  } else {
    stopCountdown();
    startInactivityDetector();
  }
});

watch(storeToRefs(authStore).isLoggedIn, (newValue) => {
  if (newValue) {
    startInactivityDetector();
  } else {
    stopInactivityDetector();
  }
});

const handleVisibilityChange = () => {
  isInFocus.value = document.visibilityState === 'visible';
};

const callAddOrUpdateNumber = () => {
  userActivityStore.addOrUpdateNumber(Date.now());
};

onMounted(async () => {
  maxInactivityInSecs.value = await fetchAutoLogoutDuration();
  window.addEventListener('click', () => {
    callAddOrUpdateNumber();
  });

  document.addEventListener('visibilitychange', handleVisibilityChange);
  handleVisibilityChange();

  if (authStore.isLoggedIn) {
    userActivityStore.addOrUpdateNumber(Date.now());
    startInactivityDetector();
  }
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('click', callAddOrUpdateNumber);
});

const fetchAutoLogoutDuration = async () => {
  return (
    parseInt(
      (await settingsStore.getSettings()).value.find(
        (setting) => setting.settingId === TechnicalSettingKeyToId.AUTO_LOGOUT_DURATION
      )?.value as string
    ) * 60
  );
};

function startInactivityDetector() {
  if (inactivityIntervalId) {
    stopInactivityDetector();
  }
  inactivityIntervalId = window.setInterval(() => {
    if (getRemainingSecondsUntilLogout() <= countdownInitial) {
      isWarningVisible.value = true;
    }
  }, 1000);
}

function stopInactivityDetector() {
  if (inactivityIntervalId) {
    window.clearInterval(inactivityIntervalId);
    inactivityIntervalId = undefined;
  }
}

function startCountdown() {
  if (countdownIntervalId) {
    stopCountdown();
  }
  countdown.value = getRemainingSecondsUntilLogout();
  countdownIntervalId = window.setInterval(() => {
    const remainingSeconds = getRemainingSecondsUntilLogout();
    if (remainingSeconds <= 0) {
      stopCountdown();
      stopInactivityDetector();
      isWarningVisible.value = false;
      router.push('/logged-out');
      authStore.logout();
    } else if (remainingSeconds > countdownInitial) {
      isWarningVisible.value = false;
    } else {
      countdown.value = getRemainingSecondsUntilLogout();
    }
  }, 1000);
}

function stopCountdown() {
  if (countdownIntervalId) {
    window.clearInterval(countdownIntervalId);
    countdownIntervalId = undefined;
  }
}

function getRemainingSecondsUntilLogout() {
  const lastInteraction = userActivityStore.getNumber() ?? Date.now();
  const inactiveSeconds = Math.floor((new Date().getTime() - lastInteraction) / 1000);

  return maxInactivityInSecs.value - inactiveSeconds;
}
</script>

<template>
  <ScuModalDialogue :is-modal="true" :is-visible="isWarningVisible && isInFocus">
    <div slot="header" class="m-0" style="min-width: 400px">
      <ScuHeadline class="m-0" element="h2">{{ t('userInactivity.warningTitle') }}</ScuHeadline>
      <hr />
    </div>

    <div slot="body" style="width: 50vw">
      {{ t('userInactivity.warningMessage', { timeout: countdown }) }}
    </div>

    <ScuBottomBar slot="footer" position="component">
      <ScuBottomBarAction
        slot="primary-action"
        :text="t('userInactivity.warningContinue')"
        @scuclick="startInactivityDetector()"
      />
    </ScuBottomBar>
  </ScuModalDialogue>
</template>
